import { useEffect, useRef, type CSSProperties } from 'react';
import { useReducedMotion } from './useReducedMotion';

type HighlightTag = 'h2' | 'h3' | 'p';

export interface ScrollHighlightProps {
  /** Statement split into words that ink in with scroll. */
  text: string;
  as?: HighlightTag;
  className?: string;
  id?: string;
  /** Viewport fraction (0–1, from the top) where the first word starts to ink. */
  start?: number;
  /** Viewport fraction where the last word is fully inked. */
  end?: number;
}

/** Statement whose words go from faint to full ink as the visitor scrolls through it (scroll-linked, rAF, one CSS variable). */
export default function ScrollHighlight({
  text,
  as: Tag = 'h2',
  className = '',
  id,
  start = 0.9,
  end = 0.45,
}: ScrollHighlightProps) {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const list = text.split(/\s+/).filter(Boolean);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reduced) {
      el.style.setProperty('--p', '1');
      el.classList.add('is-done');
      return;
    }
    el.classList.remove('is-done');

    let frame = 0;
    let active = false;
    let last = -1;

    const measure = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // progress goes 0 → 1 while the element's centre travels from `start` to `end` of the viewport
      const centre = rect.top + rect.height / 2;
      const raw = (vh * start - centre) / (vh * (start - end));
      const p = Math.min(1, Math.max(0, raw));
      const rounded = Math.round(p * 200) / 200;
      if (rounded !== last) {
        last = rounded;
        el.style.setProperty('--p', String(rounded));
      }
    };
    const onScroll = () => {
      if (active && !frame) frame = requestAnimationFrame(measure);
    };

    const io = new IntersectionObserver(([entry]) => {
      active = entry.isIntersecting;
      if (active) onScroll();
    });
    io.observe(el);
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [reduced, start, end]);

  return (
    <Tag
      ref={ref as never}
      id={id}
      className={`scroll-hl ${className}`}
      style={{ '--n': list.length } as CSSProperties}
    >
      <span className="visually-hidden">{text}</span>
      <span aria-hidden="true">
        {list.map((word, i) => (
          <span key={`${i}-${word}`}>
            <span className="scroll-hl-word" style={{ '--i': i } as CSSProperties}>{word}</span>
            {i < list.length - 1 ? ' ' : null}
          </span>
        ))}
      </span>
    </Tag>
  );
}
